import { Injectable, signal } from '@angular/core';

/** What a masked figure shows instead of an amount. */
export const AMOUNT_MASK = '••••';

/**
 * Whether the money on the garage's pages is masked.
 *
 * The console is often open on a machine at the counter, with a customer on the other
 * side of it. The toggle used to live on the dashboard and mask the dashboard alone,
 * while the registry beside it showed what every customer owes; it is one switch in the
 * header now, over every list page. The pages of one thing — a customer's card, a
 * receipt, any dialog — keep their figures: whoever opens one is working on it.
 *
 * It defaults to visible — a page whose figures are all masked reads as broken — and
 * the choice is remembered, so a counter machine is set once rather than every morning.
 */
@Injectable({ providedIn: 'root' })
export class AmountVisibilityService {
  private readonly state = signal(readHiddenPreference());

  readonly hidden = this.state.asReadonly();

  toggle(): void {
    const next = !this.state();
    this.state.set(next);
    writeHiddenPreference(next);
  }
}

/**
 * The masking preference, per browser.
 *
 * Storage can be unavailable or throw outright — a private window, site data blocked —
 * and a page must not fail to load over a display preference, so both directions are
 * guarded and the default is simply "show".
 */
const HIDDEN_KEY = 'parkingless.amountsHidden';

/** Where the dashboard kept it: read so a machine already set to hidden stays hidden. */
const DASHBOARD_KEY = 'parkingless.dashboard.revenueHidden';

function readHiddenPreference(): boolean {
  try {
    return (localStorage.getItem(HIDDEN_KEY) ?? localStorage.getItem(DASHBOARD_KEY)) === 'true';
  } catch {
    return false;
  }
}

function writeHiddenPreference(hidden: boolean): void {
  try {
    localStorage.setItem(HIDDEN_KEY, String(hidden));
  } catch {
    // A preference that cannot be remembered still works for this session.
  }
}
